import React, { useEffect, useRef, useState } from "react";
import { X, Download, Share2, Ticket, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { KW, KH, gambarKartu, renderStory, siapkanFont } from "../../lib/kartuPenerjun";
import { setSorot } from "../../lib/pulau/pesawat";
import { REGION_GEO } from "../../lib/jawselRegions";
import { glassBox, glassInner, GLASS } from "../kota/ui";

const FONT = "'Plus Jakarta Sans', sans-serif";
const btn = (utama) => ({
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, height: 42, padding: "0 16px", borderRadius: 99, border: 0, cursor: "pointer",
  fontFamily: FONT, fontSize: 13, fontWeight: 700, ...(utama ? { background: "#0000CD", color: "#fff" } : glassInner(true)), WebkitTapHighlightColor: "transparent",
});

// Lembar kaca "Kartu Penerjun": ketik @username → kartu boarding pass (canvas) + unduh/bagikan PNG story 9:16; username diingat (setSorot).
export function KartuPenerjunSheet({ awal, onClose }) {
  const [username, setUsername] = useState(awal || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const canvasRef = useRef(null);
  const offsetRef = useRef(0);

  async function cari(u) {
    const ig = (u || "").trim().replace(/^@/, "").toLowerCase();
    if (!ig) return;
    setBusy(true); setErr("");
    try { const d = await api.publicPenerjun(ig); offsetRef.current = Date.parse(d.now) - Date.now(); setData({ ...d, warna: d.warna || REGION_GEO[d.kota]?.color }); setSorot(ig); setUsername(ig); }
    catch (e) { setData(null); setErr(e.status === 404 ? "Belum ada tiket untuk username ini. Tiket terbit otomatis setelah komentar “terjun” di postingan Instagram @jawsel.fun." : e.message); }
    setBusy(false);
  }
  useEffect(() => { if (awal) cari(awal); }, []); // eslint-disable-line
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {   // segarkan status mendarat saat jamnya lewat
    if (!data || data.mendarat) return;
    const sisa = Date.parse(data.mendarat_at) - (Date.now() + offsetRef.current);
    const t = setTimeout(() => cari(data.username), Math.max(1500, sisa + 1500)); return () => clearTimeout(t);
  }, [data]); // eslint-disable-line
  useEffect(() => {
    if (!data || !canvasRef.current) return;
    let aktif = true;
    siapkanFont().then(() => {
      if (!aktif) return;
      const c = canvasRef.current, dpr = Math.min(3, window.devicePixelRatio || 1), lebar = c.clientWidth, k = lebar / (KW + 24);
      c.width = Math.round(lebar * dpr); c.height = Math.round((KH + 36) * k * dpr);
      const ctx = c.getContext("2d"); ctx.scale(dpr * k, dpr * k); ctx.clearRect(0, 0, KW + 24, KH + 36); ctx.translate(12, 8); gambarKartu(ctx, data);
    });
    return () => { aktif = false; };
  }, [data]);

  async function png() { await siapkanFont(); return new Promise(res => renderStory(data).toBlob(res, "image/png")); }
  async function unduh() {
    const b = await png(), a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `kartu-penerjun-${data.nomor_penerjun || data.username}.png`; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  }
  async function bagikan() {
    const b = await png(), file = new File([b], `kartu-penerjun-${data.nomor_penerjun}.png`, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) { try { await navigator.share({ files: [file] }); } catch { /* dibatalkan */ } }
    else unduh();
  }

  const serverNow = now + offsetRef.current;
  const sisa = data?.berangkat_at ? Date.parse(data.berangkat_at) - serverNow : -1;
  const mm = Math.floor(Math.max(0, sisa) / 60000), ss = String(Math.floor(Math.max(0, sisa) / 1000) % 60).padStart(2, "0");

  return (
    <div data-testid="kartu-sheet" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 40, background: "rgba(10,10,14,.35)", display: "flex", alignItems: "flex-end", justifyContent: "center", fontFamily: FONT }}>
      <div onClick={e => e.stopPropagation()} style={{ ...glassBox, background: GLASS.bgStrong, width: "min(440px, 100%)", maxHeight: "92vh", overflowY: "auto", borderRadius: "28px 28px 0 0", padding: "14px 18px calc(18px + env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", gap: 12, animation: "kartu-naik .32s cubic-bezier(.2,.8,.2,1)" }}>
        <div style={{ width: 40, height: 4, borderRadius: 99, background: GLASS.handle, alignSelf: "center" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 36, height: 36, borderRadius: 12, display: "grid", placeItems: "center", background: GLASS.accentBg, color: GLASS.accent }}><Ticket size={17} /></span>
          <div style={{ flex: 1 }}><b style={{ fontSize: 15 }}>Kartu Penerjun</b><span style={{ display: "block", fontSize: 11.5, color: GLASS.sub }}>Jawsel Air · District Jawsel</span></div>
          <button data-testid="kartu-tutup" onClick={onClose} aria-label="Tutup" style={{ width: 36, height: 36, borderRadius: 99, border: 0, cursor: "pointer", display: "grid", placeItems: "center", ...glassInner(true) }}><X size={16} /></button>
        </div>
        <form onSubmit={e => { e.preventDefault(); cari(username); }} style={{ display: "flex", gap: 8 }}>
          <input data-testid="kartu-username" value={username} onChange={e => setUsername(e.target.value)} placeholder="@username instagram" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            style={{ flex: 1, height: 42, padding: "0 14px", borderRadius: 99, border: "1px solid var(--g-border)", background: GLASS.inner, color: GLASS.ink, fontFamily: FONT, fontSize: 14, outline: "none", minWidth: 0 }} />
          <button type="submit" data-testid="kartu-cari" disabled={busy || !username.trim()} style={{ ...btn(true), opacity: busy || !username.trim() ? .6 : 1 }}>{busy ? <RefreshCw size={15} className="spin" /> : "Lihat"}</button>
        </form>
        {err && <p data-testid="kartu-error" style={{ margin: 0, padding: "10px 14px", borderRadius: 16, fontSize: 12.5, lineHeight: 1.5, ...glassInner(true) }}>{err}</p>}
        {data && (
          <>
            <canvas ref={canvasRef} data-testid="kartu-canvas" style={{ width: "100%", display: "block" }} />
            {sisa > 0 && <p data-testid="kartu-countdown" style={{ margin: 0, padding: "9px 14px", borderRadius: 14, fontSize: 12.5, textAlign: "center", ...glassInner(true) }}>Pesawatmu lepas landas <b>{data.berangkat_wib} WIB</b> · <b style={{ fontVariantNumeric: "tabular-nums" }}>{mm}:{ss}</b> lagi — pantau di peta</p>}
            {sisa <= 0 && !data.mendarat && <p data-testid="kartu-live" style={{ margin: 0, padding: "9px 14px", borderRadius: 14, fontSize: 12.5, textAlign: "center", ...glassInner(true) }}>Pesawatmu sedang melintas — lihat penerjun bercincin biru di peta, itu kamu.</p>}
            <div style={{ display: "flex", gap: 8 }}>
              <button data-testid="kartu-bagikan" onClick={bagikan} style={{ ...btn(true), flex: 1 }}><Share2 size={15} /> Bagikan ke story</button>
              <button data-testid="kartu-unduh" onClick={unduh} style={btn(false)} aria-label="Unduh PNG"><Download size={15} /></button>
            </div>
          </>
        )}
      </div>
      <style>{`@keyframes kartu-naik{from{transform:translateY(40px);opacity:0}to{transform:none;opacity:1}} .spin{animation:kartu-putar 1s linear infinite} @keyframes kartu-putar{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
